import { apiRequest } from '../../../shared/services/apiClient';
import {
  CreateWebWidgetRequest,
  UpdateWebWidgetRequest,
  WebWidgetAdmin,
  WebWidgetDemoConfig,
  WebWidgetSessionSettingsResponse,
  UpdateWebWidgetSessionSettingsRequest,
} from '../types/webWidgetAdmin';

const normalizeWidget = (value: unknown): WebWidgetAdmin => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  const allowedOriginsRaw = Array.isArray(payload.allowedOrigins) ? payload.allowedOrigins : [];

  return {
    id: String(payload.id ?? ''),
    name: String(payload.name ?? ''),
    widgetKey: String(payload.widgetKey ?? ''),
    welcomeTitle: String(payload.welcomeTitle ?? ''),
    welcomeMessage: String(payload.welcomeMessage ?? ''),
    launcherLabel: String(payload.launcherLabel ?? ''),
    brandName: String(payload.brandName ?? ''),
    primaryColor: String(payload.primaryColor ?? '#4F46E5'),
    isEnabled: Boolean(payload.isEnabled),
    requireVisitorEmail: Boolean(payload.requireVisitorEmail),
    requireVisitorPhone: Boolean(payload.requireVisitorPhone),
    createdAtUtc: String(payload.createdAtUtc ?? ''),
    updatedAtUtc: String(payload.updatedAtUtc ?? ''),
    channelAccountId: payload.channelAccountId ? String(payload.channelAccountId) : null,
    allowedOrigins: allowedOriginsRaw.map(item => {
      const origin = item && typeof item === 'object' ? item as Record<string, unknown> : {};
      return {
        id: String(origin.id ?? ''),
        origin: String(origin.origin ?? ''),
      };
    }),
  };
};

const pickSingle = (payload: unknown): unknown => {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }

  const envelope = payload as Record<string, unknown>;
  if (envelope.data && typeof envelope.data === 'object') {
    return envelope.data;
  }

  return payload;
};

const normalizeSessionSettingsValues = (value: unknown) => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    lifetimeHours: Number(payload.lifetimeHours ?? 24),
    absoluteMaximumLifetimeHours: Number(payload.absoluteMaximumLifetimeHours ?? 168),
    slidingExpiration: Boolean(payload.slidingExpiration ?? true),
  };
};

const normalizeSessionSettingsResponse = (value: unknown): WebWidgetSessionSettingsResponse => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    effective: normalizeSessionSettingsValues(payload.effective),
    defaults: normalizeSessionSettingsValues(payload.defaults),
    hasOverrides: Boolean(payload.hasOverrides),
  };
};

export const getWebWidgetSessionSettings = async (): Promise<WebWidgetSessionSettingsResponse> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/web-widgets/session-settings'));
  return normalizeSessionSettingsResponse(payload);
};

export const updateWebWidgetSessionSettings = async (
  request: UpdateWebWidgetSessionSettingsRequest,
): Promise<WebWidgetSessionSettingsResponse> => {
  const payload = await apiRequest<unknown>('/api/v1/web-widgets/session-settings', {
    method: 'PUT',
    body: JSON.stringify(request),
  });

  return normalizeSessionSettingsResponse(pickSingle(payload));
};

export const resetWebWidgetSessionSettings = async (): Promise<WebWidgetSessionSettingsResponse> => {
  const payload = await apiRequest<unknown>('/api/v1/web-widgets/session-settings', {
    method: 'DELETE',
  });

  return normalizeSessionSettingsResponse(pickSingle(payload));
};

export const listWebWidgets = async (): Promise<WebWidgetAdmin[]> => {
  const payload = await apiRequest<unknown>('/api/v1/web-widgets');
  const items = Array.isArray(payload) ? payload : [];
  return items.map(normalizeWidget);
};

export const getWebWidgetDemoConfig = async (): Promise<WebWidgetDemoConfig> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/web-widgets/demo-config'));
  const record = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};

  return {
    tenantCode: String(record.tenantCode ?? '').trim(),
    widgetKey: String(record.widgetKey ?? '').trim(),
    widgetName: String(record.widgetName ?? '').trim(),
  };
};

export const createWebWidget = async (request: CreateWebWidgetRequest): Promise<WebWidgetAdmin> => {
  const payload = await apiRequest<unknown>('/api/v1/web-widgets', {
    method: 'POST',
    body: JSON.stringify(request),
  });

  return normalizeWidget(pickSingle(payload));
};

export const updateWebWidget = async (widgetId: string, request: UpdateWebWidgetRequest): Promise<WebWidgetAdmin> => {
  const payload = await apiRequest<unknown>(`/api/v1/web-widgets/${widgetId}`, {
    method: 'PATCH',
    body: JSON.stringify(request),
  });

  return normalizeWidget(pickSingle(payload));
};

export const deleteWebWidget = async (widgetId: string): Promise<void> => {
  await apiRequest(`/api/v1/web-widgets/${widgetId}`, { method: 'DELETE' });
};
